import React, { useId, useRef } from 'react'
import { useField, useFormikContext } from 'formik'
import { useIntl } from 'react-intl'
import { FormGroup, MenuItem, Select } from '@theneatcompany/nui'
import PropTypes from 'prop-types'

import FieldError from '@/components/Forms/FieldError'

function SelectField({
  name,
  disabled = false,
  errorProps = {},
  labelProps = {},
  label = labelProps.label,
  placeholder,
  options = [],
  onSelect,
}) {
  const id = useId()
  const dropdownButtonRef = useRef()
  const { formatMessage } = useIntl()

  const form = useFormikContext()
  const [field, { error, touched }] = useField(name)
  const hasErrors = !!(error && touched)

  const getCurrentItem = () => options.find(item => field.value === item.value)
  const selectedLabel = getCurrentItem()?.label
    || placeholder || formatMessage({ id: 'placeholders.select' }, { label })

  const filterItem = (query, item) => `${item.label.toLowerCase()}`.indexOf(query.toLowerCase()) >= 0

  const handleItemSelect = (item) => {
    if (!item) return

    form.setFieldValue(field.name, item.value)
    if (onSelect) onSelect(item.value)
  }

  const renderItem = (item, meta) => {
    const currentItem = getCurrentItem()

    return (
      <MenuItem
        active={meta.modifiers?.active}
        selected={currentItem?.value === item.value}
        key={item.value}
        onClick={meta.handleClick}
        text={item.label}
      />
    )
  }

  return (
    <FormGroup intent={hasErrors ? 'danger' : 'default'}>
      <FormGroup.Label htmlFor={id} label={label} {...labelProps} />
      <Select
        filterable={false}
        disabled={disabled}
        id={id}
        items={options}
        itemRenderer={renderItem}
        itemPredicate={filterItem}
        onItemSelect={handleItemSelect}
        targetRef={dropdownButtonRef}
        popoverProps={{ minimal: true }}
        {...field}
      >
        {({ isOpen }) => (
          <Select.DropdownButton
            disabled={disabled}
            active={isOpen}
            ref={dropdownButtonRef}
            fill
          >
            {selectedLabel}
          </Select.DropdownButton>
        )}
      </Select>
      <FieldError message={error} label={label} touched={touched} {...errorProps} />
    </FormGroup>
  )
}

SelectField.propTypes = {
  name: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  inputProps: PropTypes.shape({}),
  errorProps: PropTypes.shape({
    hideLabel: PropTypes.bool,
    label: PropTypes.bool,
  }),
  label: PropTypes.string,
  labelProps: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
}

export default SelectField
